import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { ChoiceCard } from "@/components/PredictionMarket/MarketCardList/MultipleChoices/ChoiceCard";
import { YesNoCard } from "@/components/PredictionMarket/MarketCardList/MultipleChoices/YesNoCard";
import { usePredictionMarketEntry } from "@/hooks/predictionMarket/usePredictionMarketEntry";
import { InsufficientModal } from "@/pages/Rounds/SelectCaptain/components/InsufficientModal";
import { useAuthStore } from "@/store/auth.store";
import { ThumbaCoin } from "@roman/shared/components/Icons";
import type { PredictionMarketHistoryResponse } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketHistory";
import type { PredictionMarketChoiceWithProbability } from "@roman/shared/types/PredictionMarket";
import { cn } from "@roman/shared/utils";
import {
  PredictionMarketType,
  getPredictionMarketType,
  getYesNoLabel,
} from "@roman/shared/utils/predictionMarket/utils";
import { useEffect, useState } from "react";
import { EntryConfirmationModal } from "../../pages/PredictionMarketDetails/components/EntryConfirmationModal";
import NumberInputWithControls from "../NumberInputWithControls";
import { EntryFinishPopup } from "./EntryFinishPopup";
import { EntrySharePopup } from "./EntrySharePopup";
import { BACKGROUND_MULTIPLE_COLORS, MULTIPLE_COLORS } from "./PredictionChart/utils/constants";

interface EntryPopupProps {
  predictionMarket: PredictionMarketHistoryResponse["predictionMarket"];
  selectedChoice: PredictionMarketChoiceWithProbability;
  onClose: () => void;
}

enum ModalStatus {
  EntryCreation = "EntryCreation",
  Confirm = "Confirm",
  Finish = "Finish",
  Share = "Share",
}

export const EntryPopup: React.FC<EntryPopupProps> = ({ predictionMarket, selectedChoice, onClose }) => {
  const [modalStatus, setModalStatus] = useState<ModalStatus>(ModalStatus.EntryCreation);
  const [localSelectedChoice, _] = useState<PredictionMarketChoiceWithProbability>(selectedChoice);
  const [openInsufficientModal, setOpenInsufficientModal] = useState(false);
  const [displayedExpectedReturn, setDisplayedExpectedReturn] = useState<number | null>(null);
  const user = useAuthStore((state) => state.user);
  const userPoints = user ? Number.parseInt(user.freePoints) : 0;
  const marketType = getPredictionMarketType(predictionMarket.predictionMarketChoices);

  const {
    points,
    setPoints,
    isEntryConfirmationModalOpen,
    setIsEntryConfirmationModalOpen,
    isLoadingPotentialFreePoints,
    potentialFreePointsError,
    expectedReturn,
    handleSubmit,
    isMutating,
  } = usePredictionMarketEntry({
    predictionMarketId: predictionMarket.id,
    selectedChoice: localSelectedChoice,
  });

  // Use displayedExpectedReturn to avoid showing 0 during loading
  // Only update when we have a valid expectedReturn or when not loading
  useEffect(() => {
    if (!isLoadingPotentialFreePoints && expectedReturn >= 0) {
      setDisplayedExpectedReturn(expectedReturn);
    } else if (displayedExpectedReturn === null && expectedReturn >= 0) {
      // Initialize displayedExpectedReturn on first valid value
      setDisplayedExpectedReturn(expectedReturn);
    }
  }, [expectedReturn, isLoadingPotentialFreePoints, displayedExpectedReturn]);

  const setPointsFn = (updater: (prev: number) => number) => {
    setPoints(updater(points));
  };

  const bottomButtonText = (status: ModalStatus.EntryCreation | ModalStatus.Confirm): string => {
    switch (status) {
      case ModalStatus.EntryCreation:
        return "予想してポイントゲット！";
      case ModalStatus.Confirm:
        return "確認";
    }
  };

  const handleFinishButtonClick = async () => {
    switch (modalStatus) {
      case ModalStatus.EntryCreation:
        setModalStatus(ModalStatus.Confirm);
        break;
      case ModalStatus.Confirm:
        if (isMutating) return; // Prevent multiple submissions

        if (points > userPoints || userPoints === 0) {
          setOpenInsufficientModal(true);
          return;
        }

        await handleSubmit();

        if (!potentialFreePointsError) {
          setModalStatus(ModalStatus.Finish);
        }
        break;
      case ModalStatus.Finish:
        // TODO: Connect this to social media share.
        // https://app.asana.com/1/1206681304008283/project/1209417177573995/task/1210579522540745
        break;
      default:
        break;
    }
  };

  if (modalStatus === ModalStatus.Finish) {
    return (
      <EntryFinishPopup
        onClickShare={() => {
          setModalStatus(ModalStatus.Share);
        }}
        onClose={() => {
          onClose();
        }}
      />
    );
  }

  if (modalStatus === ModalStatus.Share) {
    return (
      <EntrySharePopup
        market={predictionMarket}
        onClose={() => {
          setModalStatus(ModalStatus.Finish);
        }}
      />
    );
  }

  return (
    <div className="w-[360px]">
      {predictionMarket.primaryImageURL && (
        <div className="w-full h-[150px]">
          <img
            src={predictionMarket.primaryImageURL}
            alt="round-image"
            className="w-full h-full object-cover rounded-t-lg"
          />
        </div>
      )}
      <div className="p-4">
        <div className="font-bold text-lg mb-2 w-full line-clamp-2">{predictionMarket.title}</div>
      </div>

      {/* Zone 2: Choice Select and manipulation buttons */}
      {modalStatus === ModalStatus.EntryCreation && (
        <div className="flex flex-col items-center">
          <NumberInputWithControls value={points} setValue={setPointsFn} throttle={400} />
        </div>
      )}
      {modalStatus === ModalStatus.Confirm && (
        <div className="flex flex-col items-center gap-4 px-4">
          {marketType === PredictionMarketType.YESNO ? (
            <YesNoCard
              id={localSelectedChoice.id}
              label={getYesNoLabel(localSelectedChoice)}
              probability={localSelectedChoice.probability}
              onClick={() => {}}
              outlined={false}
              light={true}
              className="w-[50%]"
            />
          ) : (
            <ChoiceCard
              id={localSelectedChoice.id}
              name={localSelectedChoice.name}
              probability={localSelectedChoice.probability}
              color={
                MULTIPLE_COLORS[
                  predictionMarket.predictionMarketChoices.findIndex((c) => c.id === localSelectedChoice.id) %
                    MULTIPLE_COLORS.length
                ]
              }
              backgroundColor={
                BACKGROUND_MULTIPLE_COLORS[
                  predictionMarket.predictionMarketChoices.findIndex((c) => c.id === localSelectedChoice.id) %
                    BACKGROUND_MULTIPLE_COLORS.length
                ]
              }
              onClick={() => {}}
              className="w-[80%]"
            />
          )}
          <div className="flex items-center gap-2 text-xl min-w-0">
            <div className="text-sm">消費ポイント</div>
            <div className="font-bold text-2xl">{points.toLocaleString()}</div>
            <div className="text-2xl font-bold">pt</div>
          </div>
        </div>
      )}

      <div
        className={cn(
          "text-center my-2 opacity-0",
          modalStatus === ModalStatus.Confirm && "pt-4",
          !potentialFreePointsError && "opacity-100",
        )}
      >
        <div className="flex items-center justify-center gap-2">
          <div className="flex gap-2 justify-end">
            <div className="text-base">予想獲得</div>
            <ThumbaCoin className="w-7 h-7" />
          </div>
          <div
            className={cn(
              "flex items-baseline gap-1 text-primary transition-opacity duration-100 ease-in-out py-1",
              (displayedExpectedReturn === null || isLoadingPotentialFreePoints) && "opacity-0",
            )}
          >
            <div className="text-[28px] leading-[28px] font-extrabold">
              {displayedExpectedReturn !== null ? displayedExpectedReturn.toLocaleString() : 0}
            </div>
            <div className="text-2xl font-medium">pt</div>
          </div>
        </div>
      </div>

      <div className="px-4 my-4">
        <PrimaryButton
          content={bottomButtonText(modalStatus)}
          onClick={handleFinishButtonClick}
          disabled={isLoadingPotentialFreePoints}
          className="w-full bg-gradient-primary"
        />
      </div>

      <EntryConfirmationModal open={isEntryConfirmationModalOpen} setOpen={setIsEntryConfirmationModalOpen} />
      <InsufficientModal
        open={openInsufficientModal}
        setOpen={setOpenInsufficientModal}
        entryFee={points}
        userPoints={userPoints}
      />
    </div>
  );
};
